import type { Recommendation } from '@wtta/scoring'

interface RecommendationsSectionProps {
  recommendations: Recommendation[]
  totalCostMin: number
  totalCostMax: number
  totalEffortDays: number
}

const PRIORITY_LABEL: Record<Recommendation['priority'], string> = {
  critical: 'Kritiek',
  high: 'Hoge prioriteit',
  medium: 'Medium prioriteit',
  low: 'Lage prioriteit',
}

const CURRENCY_FORMATTER = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})

export function RecommendationsSection({
  recommendations,
  totalCostMin,
  totalCostMax,
  totalEffortDays,
}: RecommendationsSectionProps) {
  if (recommendations.length === 0) {
    return (
      <section className="report-section" data-testid="recommendations-section">
        <h2 className="report-section__title">Aanbevelingen</h2>
        <p className="report-section__lead">
          Op basis van jouw quickscan zijn er geen acute actiepunten. Dat betekent niet dat je
          niets meer hoeft te doen. Controleer minstens jaarlijks of jouw certificeringen,
          contracten en processen nog actueel zijn.
        </p>
      </section>
    )
  }

  return (
    <section className="report-section" data-testid="recommendations-section">
      <h2 className="report-section__title">Aanbevelingen</h2>
      <p className="report-section__lead">
        Onderstaande {recommendations.length} acties komen voort uit jouw antwoorden, gesorteerd
        op prioriteit. Geschatte totale doorlooptijd:{' '}
        <strong>{totalEffortDays} dagen</strong>. Geschatte indicatieve kosten:{' '}
        <strong>{CURRENCY_FORMATTER.format(totalCostMin)}</strong> tot{' '}
        <strong>{CURRENCY_FORMATTER.format(totalCostMax)}</strong>.
      </p>

      <ol className="recommendation-list">
        {recommendations.map((rec) => (
          <li
            key={rec.id}
            className={`recommendation recommendation--${rec.priority}`}
            data-testid={`recommendation-${rec.id}`}
          >
            <div className="recommendation__header">
              <span className={`recommendation__priority priority-${rec.priority}`}>
                {PRIORITY_LABEL[rec.priority]}
              </span>
              <span className="recommendation__effort">~{rec.estimatedEffortDays} dagen</span>
            </div>
            <h3 className="recommendation__title">{rec.title}</h3>
            <p className="recommendation__description">{rec.description}</p>
            {rec.estimatedCostMin !== undefined && rec.estimatedCostMax !== undefined ? (
              <p className="recommendation__cost">
                Indicatieve kosten:{' '}
                {CURRENCY_FORMATTER.format(rec.estimatedCostMin)} –{' '}
                {CURRENCY_FORMATTER.format(rec.estimatedCostMax)}
              </p>
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  )
}
